"use client";

import React, { useState, useMemo } from 'react';
import { User } from 'firebase/auth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Copy, ChevronDown, ExternalLink, Loader2, Clock } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { RelativeTime } from '@/components/RelativeTime';
import { useAssistantCache } from '@/contexts/AssistantCacheContext';

// Utility function to truncate message text
const truncateMessage = (message: string, maxWords: number = 10): string => {
  const words = message.split(' ');
  if (words.length <= maxWords) return message;
  return words.slice(0, maxWords).join(' ') + '...';
};

interface AssistantSwitcherProps {
  currentAssistantId: string;
  currentAssistantName: string;
  currentAssistantAvatar: string;
  currentUser: User | null;
  onNewChat: () => Promise<void>; // Callback for duplicating current assistant (now async)
}

export function AssistantSwitcher({
  currentAssistantId,
  currentAssistantName,
  currentAssistantAvatar,
  currentUser,
  onNewChat,
}: AssistantSwitcherProps) {
  const [duplicating, setDuplicating] = useState(false);
  const router = useRouter();
  const { assistants: cachedAssistants, loading } = useAssistantCache();

  // Filter out current assistant and get top 10
  const assistants = useMemo(() => {
    const filtered = cachedAssistants.filter(a => a.assistantId !== currentAssistantId);
    console.log('[AssistantSwitcher] Cached assistants:', cachedAssistants.length, 'filtered:', filtered.length);
    console.log('[AssistantSwitcher] Sample assistant:', filtered[0]);
    return filtered;
  }, [cachedAssistants, currentAssistantId]);

  const handleSwitchAssistant = (assistantId: string) => {
    router.push(`/assistant/${assistantId}`);
  };

  const handleNewChat = async () => {
    setDuplicating(true);

    // Safeguard: Reset loading state after 15 seconds no matter what
    const timeoutId = setTimeout(() => {
      console.warn('New chat creation timed out, resetting loading state');
      setDuplicating(false);
    }, 15000);

    try {
      await onNewChat();
      clearTimeout(timeoutId);
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Error creating new chat:', error);
      setDuplicating(false); // Reset on error, otherwise redirect will happen
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="px-4 gap-2" size="lg" disabled={duplicating}>
          {duplicating ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Copy className="h-5 w-5" />
          )}
          <span>{duplicating ? 'Creating...' : 'New Chat'}</span>
          {!duplicating && <ChevronDown className="h-4 w-4 opacity-50" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[320px]">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Continue with current assistant
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={handleNewChat}
          className="gap-2 py-2 cursor-pointer"
          disabled={duplicating}
        >
          {duplicating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Avatar className="h-6 w-6 flex-shrink-0">
              <AvatarImage src={currentAssistantAvatar} alt={currentAssistantName} />
              <AvatarFallback className="text-xs">
                {currentAssistantName.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          )}
          <div className="flex-1">
            <div className="font-medium text-sm">
              {duplicating ? 'Creating new chat...' : 'New Chat (Duplicate)'}
            </div>
            {!duplicating && (
              <div className="text-xs text-muted-foreground">
                Continue with {currentAssistantName}
              </div>
            )}
          </div>
        </DropdownMenuItem>

        {assistants.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
              Or switch to another assistant
            </DropdownMenuLabel>
            <div className="max-h-[400px] overflow-y-auto">
              {assistants.slice(0, 10).map((assistant) => (
                <DropdownMenuItem
                  key={assistant.assistantId}
                  onClick={() => handleSwitchAssistant(assistant.assistantId)}
                  className="gap-2 py-3 cursor-pointer items-start"
                >
                  <Avatar className="h-8 w-8 flex-shrink-0 mt-0.5">
                    <AvatarImage src={assistant.avatar} alt={assistant.name} />
                    <AvatarFallback className="text-xs">
                      {assistant.name.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm truncate">{assistant.name}</div>
                    </div>
                    {/* Show last used timestamp */}
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                      {assistant.lastAccessedAt && (
                        <span className="flex items-center gap-0.5">
                          <Clock className="h-3 w-3" />
                          <span className="font-medium">Used:</span>
                          <RelativeTime date={new Date(assistant.lastAccessedAt)} />
                        </span>
                      )}
                    </div>
                    {assistant.lastMessage ? (
                      <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                        {truncateMessage(assistant.lastMessage)}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">
                        No messages yet
                      </p>
                    )}
                  </div>
                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 flex-shrink-0 mt-1" />
                </DropdownMenuItem>
              ))}
            </div>
            {assistants.length > 10 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => router.push('/')}
                  className="gap-2 py-2 cursor-pointer justify-center text-xs text-muted-foreground hover:text-foreground"
                >
                  <span>View All {assistants.length} Assistants</span>
                  <ExternalLink className="h-3 w-3" />
                </DropdownMenuItem>
              </>
            )}
          </>
        )}

        {loading && (
          <DropdownMenuLabel className="text-xs text-muted-foreground text-center">
            Loading assistants...
          </DropdownMenuLabel>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
